import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  memo,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useDeepCompareEffect } from "use-deep-compare";
import {
  CargoPackagingProps,
  CustomProps,
  DeliveryProps,
  FcrProps,
  UnloadingProps,
  WeighingPops,
  FiltersType,
} from "./types";
import {
  PaginationModel,
  ShippingCaseResponse,
} from "@pages/shippingView/types";
import { RootState, useAppDispatch } from "@store/index";
import {
  cleanData,
  exportPackingList,
  fetchPacking,
  getFailure,
  hideWarning,
  preloadFileByType,
  updatePackingList,
} from "./packingSlice";
import { selectPackingView } from "@pages/shippingView/shippingSlice";
import PackingColumns from "./components/packingColumns";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import { Box, Button, CircularProgress } from "@mui/material";
import Warning from "@atoms/warning";
import { FileDownloadOutlined, FileUploadOutlined } from "@mui/icons-material";
import {
  columnOptions,
  mapCargoPacking,
  mapCustom,
  mapDelivery,
  mapFcr,
  mapUnloadig,
  mapWeighing,
  processPatchingPacking,
} from "./helpers/map-cargo-packing";
import { packingFilter } from "./config";
import BlankShippingCase from "@pages/shippingView/components/blankShippingCase";
import Filters from "./components/filters";
import PackingListEditForm from "./components/packingListEditForm";
import {
  buildEditingDataFromItem,
  buildPackingPatchPayload,
} from "./helpers/packing-edit-fields";
import { useVisibleColumns } from "@hooks/use-visible-columns";
import { Footer } from "@atoms/meta";
import CancelDialog from "./components/cancelDialog";
import normalizeParams from "@pages/liteWms/helpers/normalize";
import { getStoredPageSize, setStoredPageSize } from "@hooks/use-grid-pagesize";
import { useIsTablet } from "@hooks/use-is-tablet";
import { useFilterListWithRequiredValues } from "@hooks/use-page-filters-values";
import { useSidebar } from "@context/sidebar-context";
import TabletColumnSwipe from "@pages/liteTms/components/tabletColumnSwipe";
import {
  getTabletColumnGroups,
  getTabletPinnedColumn,
} from "./helpers/packing-tablet-columns";
import {
  PACKING_TABLET_ROW_HEIGHT,
  packingTabletFiltersSx,
} from "./helpers/tablet-compact-styles";
import { canEditPackingList } from "@pages/shippingView/helpers/tabs-mapping";

const PAGE_SIZE = 10;
const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const PackingList: React.FC = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isTablet = useIsTablet();
  const { isOpen: isSidebarOpen } = useSidebar();
  const { oid, sid } = useParams();
  const [delayedSidebarOpen, setDelayedSidebarOpen] = useState(isSidebarOpen);
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingRow, setEditingRow] = useState<Record<string, any> | null>(
    null,
  );
  const [editingData, setEditingData] = useState<
    Partial<
      CargoPackagingProps &
        FcrProps &
        UnloadingProps &
        WeighingPops &
        CustomProps &
        DeliveryProps
    >
  >({});
  const [initialEditingData, setInitialEditingData] = useState<
    Partial<
      CargoPackagingProps &
        FcrProps &
        UnloadingProps &
        WeighingPops &
        CustomProps &
        DeliveryProps
    >
  >({});
  const [filterList, setFilterList, filterOptions, setFilterOptions] =
    useFilterListWithRequiredValues<FiltersType>(
      "packing_list",
      {},
      undefined,
      sid,
      packingFilter.flatMap((section) => section.filters.map((f) => f.id)),
    );
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: getStoredPageSize("packing_list", PAGE_SIZE),
  });
  const [sortingModel, setSortingModel] = useState<GridSortModel>([]);
  const [visibleColumns, setVisibleColumns] = useVisibleColumns(
    "sc_packing_list",
    columnOptions.map((c) => c.id),
  );
  const [rowToUnlock, setRowToUnlock] = useState<CargoPackagingProps | null>(
    null,
  );
  const [columnGroupIndex, setColumnGroupIndex] = useState(0);

  const hasFilters = Object.keys(filterList).length;

  const { shipping } = useOutletContext<{ shipping: ShippingCaseResponse }>();
  const {
    loading,
    items,
    success,
    error,
    total,
    uploading,
    exporting,
    meta,
    currentParams,
  } = useSelector(selectPackingView);

  useEffect(() => {
    setStoredPageSize("packing_list", paginationModel.pageSize);
  }, [paginationModel.pageSize]);

  useEffect(() => {
    if (isSidebarOpen) {
      setDelayedSidebarOpen(true);
    } else {
      const timer = setTimeout(() => setDelayedSidebarOpen(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    if (success && editingId) {
      setEditingId(null);
      setEditingRow(null);
      setEditingData({});
      setInitialEditingData({});
    }
  }, [success, editingId]);

  const requestParams = useMemo(
    () => normalizeParams(filterList),
    [filterList],
  );

  const getOrderBy = useMemo(
    () =>
      (sortModel: GridSortModel, columns: GridColDef[]): string | undefined => {
        if (sortModel.length === 0) return undefined;
        const { field, sort } = sortModel[0];
        const col = columns.find((c) => c.field === field);
        const sortField = (col as any)?.sortField || field;
        return sort === "desc" ? `-${sortField}` : sortField;
      },
    [],
  );

  useEffect(() => {
    return () => {
      dispatch(cleanData());
    };
  }, []);

  const handlePaginationChange = useCallback((model: PaginationModel) => {
    setPaginationModel(model);
  }, []);

  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    setSortingModel(sortModel);
  }, []);

  const updateFields = useCallback((value: Partial<CargoPackagingProps>) => {
    setEditingData((prev) => ({
      ...prev,
      ...value,
    }));
  }, []);

  const handleEdit = useCallback(
    (row: any) => {
      const item = items?.find((i) => i.id === row.id);
      if (!item) return;
      const nextEditData = buildEditingDataFromItem(item);
      setEditingId(row.id);
      setEditingRow(row);
      setEditingData(nextEditData);
      setInitialEditingData(nextEditData);
    },
    [items],
  );

  const handleCancel = useCallback(() => {
    setEditingId(null);
    setEditingRow(null);
    setEditingData({});
    setInitialEditingData({});
  }, []);

  const handleSave = useCallback(() => {
    const warehouse =
      items?.find((item) => item.id === editingId)?.warehouse || null;
    const changedData = buildPackingPatchPayload(
      editingData as Record<string, any>,
      initialEditingData as Record<string, any>,
    );
    const cleanedData = processPatchingPacking(changedData);
    if (warehouse) {
      cleanedData.warehouse = warehouse;
    }

    dispatch(
      updatePackingList({ id: editingId as number, params: cleanedData }),
    );
  }, [dispatch, editingData, editingId, initialEditingData, items]);

  const rows = items?.map((item) => ({
    id: item.id,
    ...mapFcr(item),
    ...mapCustom(item),
    ...mapUnloadig(item),
    ...mapWeighing(item),
    ...mapDelivery(item),
    ...mapCargoPacking(item),
  }));

  const handleUnlock = useCallback((row: CargoPackagingProps) => {
    setRowToUnlock(row);
  }, []);

  const columnGroups = useMemo(
    () => (isTablet ? getTabletColumnGroups(visibleColumns) : []),
    [isTablet, visibleColumns],
  );
  const columnGroupCount = columnGroups.length;

  useEffect(() => {
    if (columnGroupIndex >= columnGroupCount) {
      setColumnGroupIndex(0);
    }
  }, [columnGroupCount, columnGroupIndex]);

  useEffect(() => {
    setColumnGroupIndex(0);
  }, [visibleColumns]);

  const pinnedColumn = useMemo(
    () => (isTablet ? getTabletPinnedColumn(visibleColumns) : null),
    [isTablet, visibleColumns],
  );

  const displayColumnIds = useMemo(() => {
    if (!isTablet) return visibleColumns;
    return columnGroups[columnGroupIndex] ?? [];
  }, [isTablet, visibleColumns, columnGroups, columnGroupIndex]);

  const columns = useMemo(
    () =>
      PackingColumns(
        role as string,
        handleEdit,
        handleUnlock,
        displayColumnIds,
        { isTablet, pinnedColumn },
      ),
    [role, handleEdit, handleUnlock, displayColumnIds, isTablet, pinnedColumn],
  );

  const handleColumnGroupChange = useCallback(
    (index: number) => {
      setColumnGroupIndex(index);
      setSortingModel((prev) => {
        if (prev.length === 0) return prev;
        const sortField = prev[0].field;
        const groupFields = columnGroups[index] ?? [];
        return groupFields.includes(sortField) ? prev : [];
      });
    },
    [columnGroups],
  );

  useDeepCompareEffect(() => {
    dispatch(
      fetchPacking({
        id: shipping.id,
        params: {
          ...requestParams,
          order_by: getOrderBy(sortingModel, columns),
          page: paginationModel.page + 1,
          size: paginationModel.pageSize,
        },
      }),
    );
  }, [
    requestParams,
    sortingModel,
    shipping.id,
    paginationModel.page,
    paginationModel.pageSize,
  ]);

  const memoFilter = useMemo(
    () => (
      <Filters
        sid={Number(sid)}
        columns={visibleColumns}
        filters={filterList}
        setFilters={setFilterList}
        page="packing_list"
        setColumns={(newCols) => setVisibleColumns(newCols)}
        filterObject={filterOptions}
        setFilterObject={setFilterOptions}
      />
    ),
    [
      sid,
      visibleColumns,
      filterList,
      setFilterList,
      filterOptions,
      setFilterOptions,
      setVisibleColumns,
    ],
  );

  const handleDownloadClick = () => {
    dispatch(exportPackingList(shipping.id, currentParams || undefined));
  };
  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files;
    if (!file) return;

    if (file[0]?.size > MAX_FILE_SIZE_BYTES) {
      dispatch(
        getFailure({
          error: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Please upload a smaller file.`,
        }),
      );
      return;
    }
    dispatch(preloadFileByType(file[0], shipping.id, paginationModel.pageSize));
    e.target.value = "";
  };

  const closeCallback = () => dispatch(hideWarning());

  const gridHeightStyle = (() => {
    if (total < 8) return "auto";
    return isTablet ? "calc(100vh - 280px)" : "calc(100vh - 410px)";
  })();

  const grid = (
    <Box sx={{ height: gridHeightStyle }}>
      <DataGrid
        key={
          isTablet
            ? `packing-tablet-cols-${columnGroupIndex}`
            : "packing-desktop-cols"
        }
        loading={loading}
        disableVirtualization={true}
        className={`packing tabbed${isTablet ? " lite-tms-tablet" : ""}${isTablet && delayedSidebarOpen ? " sidebar-open" : ""}`}
        columns={columns}
        rows={rows}
        rowCount={total}
        getRowId={(row) => row.id}
        rowHeight={isTablet ? PACKING_TABLET_ROW_HEIGHT : undefined}
        columnHeaderHeight={isTablet ? PACKING_TABLET_ROW_HEIGHT : undefined}
        sx={{
          mt: isTablet ? 0.5 : "16px",
          width: "100%",
          ...(isTablet && {
            "--DataGrid-rowHeight": `${PACKING_TABLET_ROW_HEIGHT}px`,
          }),
        }}
        filterMode="server"
        sortingMode="server"
        paginationMode="server"
        sortingOrder={["asc", "desc"]}
        pageSizeOptions={[10, 25, 50]}
        isCellEditable={() => false}
        paginationModel={paginationModel}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handlePaginationChange}
        slots={{
          footer: () => <Footer meta={meta} />,
        }}
      />
    </Box>
  );
  return (
    <Box position="relative">
      {!!error && (
        <Warning text={error} severity="error" closeCallback={closeCallback} />
      )}
      {(!!success || !!uploading.success) && (
        <Warning
          text={success || (uploading.success as string)}
          severity="success"
          closeCallback={closeCallback}
        />
      )}
      {loading && !items && <CircularProgress />}
      {!items?.length && !loading && !Object.keys(filterList).length && (
        <BlankShippingCase
          type="cargoDetails"
          text="No cargo details or tracking information found."
          subtext="To get started, please click the button below."
          init={
            !["Customer", "WMS+Operations"].includes(role as string)
              ? () =>
                  navigate(
                    `/offers/${oid}/shipping-case/${sid}/cargo-details/commodity`,
                  )
              : undefined
          }
        />
      )}
      {(!!items?.length || !!hasFilters) && (
        <>
          {!isTablet && (
            <Box gap={2} sx={{ display: "flex", mb: 2 }}>
              {canEditPackingList(role as string) && (
                <>
                  <Button
                    size="small"
                    disabled={uploading.loading}
                    startIcon={
                      !uploading.loading ? (
                        <FileUploadOutlined />
                      ) : (
                        <CircularProgress size={18} />
                      )
                    }
                    sx={{ color: "rgba(0, 0, 0, 0.87)" }}
                    onClick={() => inputRef.current?.click()}
                  >
                    Import
                  </Button>
                  <input
                    type="file"
                    hidden
                    ref={inputRef}
                    onChange={handleFiles}
                    accept=".xml,.xlsx,.xls"
                  />
                </>
              )}

              <Button
                size="small"
                startIcon={
                  !exporting.loading ? (
                    <FileDownloadOutlined />
                  ) : (
                    <CircularProgress size={18} />
                  )
                }
                sx={{ color: "rgba(0, 0, 0, 0.87)" }}
                onClick={handleDownloadClick}
              >
                Download
              </Button>
            </Box>
          )}
          <Box
            sx={{
              mt: isTablet ? 0 : undefined,
              gap: isTablet ? 0.5 : undefined,
            }}
          >
            <Box sx={isTablet ? packingTabletFiltersSx : undefined}>
              {memoFilter}
            </Box>
            {isTablet ? (
              <TabletColumnSwipe
                groupIndex={columnGroupIndex}
                groupCount={columnGroupCount}
                onGroupChange={handleColumnGroupChange}
              >
                {grid}
              </TabletColumnSwipe>
            ) : (
              grid
            )}
          </Box>
        </>
      )}
      {rowToUnlock && (
        <CancelDialog
          title=""
          text={`Are you sure you want to update this record in the "${
            (rowToUnlock as any).status_delivery === "BOOKED"
              ? "Booked"
              : "Delivered"
          }" status?`}
          handleClose={() => setRowToUnlock(null)}
          onConfirm={() => {
            handleEdit(rowToUnlock);
            setRowToUnlock(null);
          }}
        />
      )}
      <PackingListEditForm
        open={editingId != null}
        onClose={handleCancel}
        onSave={handleSave}
        data={editingData}
        onChange={updateFields}
        isContainer={editingRow?.packing_list_generation_type === "CONTAINERS"}
      />
    </Box>
  );
};

export default memo(PackingList);
